import { Col, Grid, Row, RowWrap } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import getOr from "lodash/fp/getOr";
import React, { ReactElement } from "react";
import { IVendorQuery } from "~/lib/queries/__generated__/VendorQuery.generated";
import { IConnectTargetType, IVendorUser } from "~/types/types";
import { useSelfie } from "~/components/auth/Auth";
import Cover, { PLACEHOLDER_COVER } from "~/components/cover/Cover";
import Feed from "~/components/feed/Feed";
import ActivityIcon from "~/components/icons/VendorProfile/ActivityIcon";
import GovernmentsIcon from "~/components/icons/VendorProfile/GovernmentsIcon";
import ProductsIcon from "~/components/icons/VendorProfile/ProductsIcon";
import ProjectsIcon from "~/components/icons/VendorProfile/ProjectsIcon";
import userHasSearchAccess from "~/components/makers/userHasSearchAccess";
import Team from "~/components/profile/Team";
import FullVendorProjects from "~/components/project/FullVendorProjects";
import VendorGovernmentConnections from "~/components/vendor/tabs/VendorGovernmentConnections";
import VendorHighlights from "~/components/vendor/tabs/VendorHighlights";
import VendorProducts from "~/components/vendor/tabs/VendorProducts";
import GovernmentConnectionsBox from "~/components/vendor/profile/GovernmentConnectionsBox";
import VendorActions from "~/components/vendor/profile/VendorActions";
import VendorInfo from "~/components/vendor/profile/VendorInfo";
import VendorProfileNavbar from "~/components/vendor/profile/VendorProfileNavbar";
import VendorProfilePreviewHeader from "~/components/vendor/profile/VendorProfilePreviewHeader";
import VendorUpgradeRequired from "~/components/vendor/profile/VendorUpgradeRequired";
import { FormattedMessage } from "react-intl";
import StoriesIcon from "~/components/icons/VendorProfile/StoriesIcon";
import VendorStories from "~/components/vendor/VendorStories";

interface IVendorProfileProps {
  vendor: NonNullable<IVendorQuery["company"]>;
  tab: string;
  preview?: "makers" | "current" | null;
  translationButton?: () => ReactElement | null;
}

export default function VendorProfile({ vendor, tab, preview, translationButton }: IVendorProfileProps) {
  const user = useSelfie();

  const viewAccess = userHasSearchAccess(user) || vendor.viewerIsMember;
  const upgradeRequired = user && user.__typename === "VendorUser" && !viewAccess;
  const isPreviewing =
    user && user.__typename === "VendorUser" && user.company && user.company._id === vendor._id && preview !== null;
  const isPreviewingAsMaker = preview === "makers";
  const isPreviewingAsBasic = preview === "current";
  const showCover = (vendor.cover && vendor.cover.original) || (!preview && vendor.viewerIsMember);
  const canShowAllTabs = (vendor.plan.limits.enrichedProfile || isPreviewingAsMaker) && !isPreviewingAsBasic;
  const canShowGovernmentsTab = user && user.__typename === "GovernmentUser" && canShowAllTabs;
  const userHasOwnerRights =
    user &&
    user.__typename === "VendorUser" &&
    vendor.viewerIsMember &&
    (user.role === "COMPANY_OWNER" || vendor.usersCanAccessAccountPage);

  // Component is being shown to a vendor user who has no view rights
  if (upgradeRequired) {
    return <VendorUpgradeRequired user={user as IVendorUser} />;
  }

  return (
    <div>
      <Grid>
        {isPreviewing && (
          <VendorProfilePreviewHeader
            vendorSlug={vendor.slug}
            userHasOwnerRights={userHasOwnerRights || false}
            preview={preview}
            isEnrichedProfile={vendor.plan.limits.enrichedProfile}
          />
        )}

        {showCover && (
          <Row mb={[20, 20, 20, 20]}>
            <Col width={[1, 1, 1, 1]}>
              <div
                css={{
                  position: "relative",
                }}
              >
                <Cover
                  alt="Cover"
                  height={250}
                  onRemove={() => {}}
                  onChange={() => {}}
                  onReposition={() => {}}
                  originalSource={getOr(null, "cover.original", vendor)}
                  repositionedSource={getOr(null, "cover.repositioned", vendor)}
                  placeholderSource={PLACEHOLDER_COVER}
                  readOnly={true}
                  containerProps={{
                    style: {
                      borderRadius: 5,
                      overflow: "hidden",
                    },
                  }}
                />
              </div>
            </Col>
          </Row>
        )}

        <RowWrap>
          <Col width={[1, 1, 4 / 12, 4 / 12]}>
            <div
              css={{
                display: "grid",
                gridRowGap: 24,
              }}
            >
              <VendorInfo vendor={vendor} preview={preview} translationButton={translationButton} />

              {vendor.membersCount > 0 && user?.__typename === "GovernmentUser" && (
                <Team
                  members={vendor.members}
                  viewerIsMember={vendor.viewerIsMember || false}
                  viewerCanInviteMembers={false}
                  viewerCanRemoveMembers={false}
                  targetType="VENDOR"
                  targetId={vendor._id}
                />
              )}

              {vendor.governmentConnectionsCount > 0 && <GovernmentConnectionsBox vendor={vendor} />}
            </div>
          </Col>
          <Col width={[1, 1, 8 / 12, 8 / 12]} mt={[24, 24, 0, 0]}>
            <VendorProfileNavbar
              tab={tab}
              vendorSlug={vendor.slug}
              preview={preview}
              canShowAllTabs={canShowAllTabs}
              canShowGovernmentsTab={!!canShowGovernmentsTab}
              feedCount={vendor.feedCount}
              productsCount={vendor.productsCount || 0}
              storiesCount={vendor.storiesCount || 0}
              projectsCount={vendor.projectsCount || 0}
              governmentConnectionsCount={vendor.governmentConnectionsCount}
              highlightsCount={vendor.highlightsCount + vendor.newsworthyCount}
            />

            <div
              css={{
                marginTop: 24,
              }}
            >
              {tab === "connect" && (
                <div>
                  {vendor.feedCount === 0 && (
                    <div
                      css={{
                        borderRadius: 4,
                        border: `1px solid ${palette.lightestGray}`,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        padding: 16,
                        background: "#fff",
                      }}
                    >
                      <ActivityIcon width={32} height={32} color={palette.darkGray} />

                      <p
                        css={{
                          margin: "12px 0 0 0",
                          fontSize: 18,
                          fontWeight: 500,
                          color: palette.darkGray,
                        }}
                      >
                        <FormattedMessage defaultMessage="No Activity" id="vugtF7" />
                      </p>
                    </div>
                  )}

                  <div
                    css={{
                      display: "grid",
                      gridRowGap: 16,
                    }}
                  >
                    <Feed targetId={vendor._id} targetType={IConnectTargetType.Vendor} showNoPosts={false} />
                  </div>
                </div>
              )}

              {tab === "products" && (
                <div>
                  {vendor.productsCount === 0 && (
                    <div
                      css={{
                        borderRadius: 4,
                        border: `1px solid ${palette.lightestGray}`,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        padding: 16,
                        background: "#fff",
                      }}
                    >
                      <ProductsIcon width={32} height={32} color={palette.darkGray} />

                      <p
                        css={{
                          margin: "12px 0 0 0",
                          fontSize: 18,
                          fontWeight: 500,
                          color: palette.darkGray,
                        }}
                      >
                        <FormattedMessage defaultMessage="No Products" id="Gg4+K7" />
                      </p>
                    </div>
                  )}

                  {(vendor.products || []).length > 0 && <VendorProducts vendor={vendor} />}
                </div>
              )}

              {tab === "governments" && (
                <div>
                  {vendor.governmentConnectionsCount === 0 && (
                    <div
                      css={{
                        borderRadius: 4,
                        border: `1px solid ${palette.lightestGray}`,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        padding: 16,
                        background: "#fff",
                      }}
                    >
                      <GovernmentsIcon width={32} height={32} color={palette.darkGray} />

                      <p
                        css={{
                          margin: "12px 0 0 0",
                          fontSize: 18,
                          fontWeight: 500,
                          color: palette.darkGray,
                        }}
                      >
                        <FormattedMessage defaultMessage="No Governments" id="TXTwHh" />
                      </p>
                    </div>
                  )}
                  <VendorGovernmentConnections showUpgradeBanner={false} showPlaceholder={false} vendor={vendor} />
                </div>
              )}

              {tab === "stories" && (
                <div>
                  {vendor.storiesCount === 0 && false && (
                    <div
                      css={{
                        borderRadius: 4,
                        border: `1px solid ${palette.lightestGray}`,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        padding: 16,
                        background: "#fff",
                      }}
                    >
                      <StoriesIcon width={32} height={32} color={palette.darkGray} />

                      <p
                        css={{
                          margin: "12px 0 0 0",
                          fontSize: 18,
                          fontWeight: 500,
                          color: palette.darkGray,
                        }}
                      >
                        <FormattedMessage defaultMessage="No Stories" id="7WztI4" />
                      </p>
                    </div>
                  )}

                  <VendorStories vendor={vendor} showUpgradeBanner={false} />
                </div>
              )}

              {tab === "projects" && (
                <div>
                  {vendor.projectsCount === 0 && (
                    <div
                      css={{
                        borderRadius: 4,
                        border: `1px solid ${palette.lightestGray}`,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        padding: 16,
                        background: "#fff",
                      }}
                    >
                      <ProjectsIcon width={32} height={32} color={palette.darkGray} />

                      <p
                        css={{
                          margin: "12px 0 0 0",
                          fontSize: 18,
                          fontWeight: 500,
                          color: palette.darkGray,
                        }}
                      >
                        <FormattedMessage defaultMessage="No Projects" id="E4723/" />
                      </p>
                    </div>
                  )}
                  <FullVendorProjects showUpgradeBanner={false} showPlaceholder={false} vendor={vendor} />
                </div>
              )}

              {tab === "actions" && (
                <div>
                  <VendorActions vendor={vendor} />
                </div>
              )}

              {tab === "highlights" && <VendorHighlights vendor={vendor} />}
            </div>
          </Col>
        </RowWrap>
      </Grid>
    </div>
  );
}
